/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import * as _ from 'lodash';
import { FormattedMessage } from 'react-intl';

import { Caption1 } from '../../../components/Text';

import healthEntryTextFormatter from '../../../../models/HealthEntryTextFormatting';
import ShareCardIncludedTypeNames from '../../../../constants/ShareCardIncludedTypeNames';
import HealthEntryNameText from '../../../components/HealthEntryNameText';
import ShareCardSummaryList from './ShareCardSummaryList';
import { iTrackable, TrackableModel } from '../../../../apiServices/apiModels/Trackable';
import { TrackableShareCardSummary } from '../../components/attributes/OccurrenceSummary';
import { useAllHealthEntryTrackablesForActiveProfile, useTrackableListForActiveProfile } from '../../../hooks/useTrackable';
import { useHealthEntriesForActiveProfile } from '../../../hooks/useHealthEntry';
import { generalHealthEntry } from '../../../../models/HealthEntryCompose';
import ShareCardModel from '../../../../models/ShareCardModel';

interface Props {
  shareCard: any;
  collection: any[];
  title: any;
  languageCode: string;
  includedValues: ShareCardIncludedTypeNames;
}
export const ShareCardSummaryHealthEntries: React.FC<Props> = (props) => {

  const [{ trackableIdsForHeLookup }] = useAllHealthEntryTrackablesForActiveProfile();
  const [, { getTrackableTermName }] = useTrackableListForActiveProfile();

  if (props.collection.length === 0) {
    return null;
  }
  const includedTrackableIds = ShareCardModel.getIncludedTrackableIds(props.shareCard.data).reduce((acc, v) => ({ ...acc, [v]: true }), {});

  return (
    <div className="cmp-sharecardsummary-healthentry">
      {props.title ? <Caption1>{props.title}</Caption1> : null}

      <ShareCardSummaryList
        items={props.collection}
        renderItem={(he) => {
          const fullSummaryList = _.compact(healthEntryTextFormatter(he, props.includedValues, props.shareCard.definition).fullSummaryList(props.languageCode));
          fullSummaryList.shift();
          const tIds = (trackableIdsForHeLookup[he.id] || []).filter(id => includedTrackableIds[id]);
          const hasTrackables = tIds.length > 0;

          return (
            <div>
              <div className="font-body" style={{ marginBottom: '4px' }}>
                <HealthEntryNameText
                  healthEntry={he}
                  includedValues={props.includedValues}
                  languageCode={props.languageCode}
                />
              </div>
              {
                fullSummaryList.map((s, index) => (
                  <div
                    key={index}
                    style={{ marginBottom: (index < fullSummaryList.length - 1) ? 4 : 0 }}
                    className="font-footnote font-color-gsDark"
                  >
                    {s}
                  </div>
                ))
              }
              {
                hasTrackables ? (
                  <div className="font-footnote font-color-gsMedDark" style={{ marginTop: 4 }}>
                    <FormattedMessage
                      id="share.relatedToTrackable.label"
                      values={{
                        string1: tIds.map(id => getTrackableTermName(id, props.languageCode)).sort().join(', '),
                      }}
                    />
                  </div>
                ) : null
              }
            </div>
          );
        }}
      />
    </div>
  );
};

interface ShareCardSummaryTrackablesProps {
  title: any;
  shareCard: any;
  trackables: iTrackable[];
  terms: any;
  languageCode: string;
  includedTypeValue: ShareCardIncludedTypeNames;
}

export const ShareCardSummaryTrackables: React.FC<ShareCardSummaryTrackablesProps> = (props) => {
  const { title, shareCard, trackables, terms, languageCode, includedTypeValue } = props;

  const [{ heIdsForTrackableLookup }] = useAllHealthEntryTrackablesForActiveProfile();
  const [{ healthEntryLookup }] = useHealthEntriesForActiveProfile();

  const includedHEs = (shareCard.included_health_entry_values || [])
    .reduce((acc, v) => ({ ...acc, [v.health_entry_id]: true }), {});

  if (trackables.length === 0) { return null; }

  return (
    <div className="cmp-sharecardsummary-healthentry">
      {title ? <Caption1>{title}</Caption1> : null}

      <ShareCardSummaryList
        items={trackables}
        renderItem={(t) => {
          const heIds = heIdsForTrackableLookup[t.id] || [];
          const hes = heIds
            .filter(id => (healthEntryLookup[id] && includedHEs[id]))
            .map(id => {
              const heData = healthEntryLookup[id];
              const he = generalHealthEntry(heData, languageCode);
              return he.medliTerm.formattedTermName();
            });
          const heNames = hes.sort((a, b) => a.localeCompare(b)).join(', ');
          const isHEIncluded = heNames.length > 0;
          const trackable = new TrackableModel(t, null, terms);
          const commentText = trackable.commentText;

          return (
            <>
              <TrackableShareCardSummary
                trackable={t}
                showComments={false}
                showFlagged={false}
                terms={terms}
                includedTypeValue={includedTypeValue}
                languageCode={languageCode}
              />
              {
                isHEIncluded ? (
                  <div className="font-footnote font-color-gsMedDark" style={{ marginTop: 4 }}>
                    <FormattedMessage id="share.relatedFromTrackable.label" values={{ string1: heNames }} />
                  </div>
                ) : null
              }

              {
                commentText ? (
                  <div className="font-footnote font-color-gsMedDark" style={{ marginTop: 4 }}>
                    {commentText}
                  </div>
                ) : null
              }
            </>
          );
        }}
      />
    </div>
  );
};
